import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  ArrowLeft,
  Save,
  Loader2
} from 'lucide-react';

const EditContract = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [contractTerms, setContractTerms] = useState(['1yr', '2yr', '3yr', '4yr', '5yr']);
  
  const [formData, setFormData] = useState({
    title: '',
    document_type: 'contract',
    content: '',
    client_id: '',
    requires_signature: true,
    start_date: '',
    end_date: '',
    contract_term: '',
    value: '',
    currency: 'USD',
    status: 'draft'
  });

  useEffect(() => {
    fetchContract();
    fetchClients();
    fetchContractTerms();
  }, [id]);

  const fetchContract = async () => {
    try {
      const res = await api.get(`/contracts/${id}`);
      const contract = res.data.contract;
      
      setFormData({
        title: contract.title || '',
        document_type: contract.document_type || 'contract',
        content: contract.content || '',
        client_id: contract.client_id || '',
        requires_signature: contract.requires_signature !== false,
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        contract_term: '',
        value: contract.value || '',
        currency: contract.currency || 'USD',
        status: contract.status || 'draft'
      });
    } catch (error) {
      console.error('Failed to load contract:', error);
      toast.error('Failed to load contract');
      navigate('/contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const fetchContractTerms = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.contract_terms && res.data.contract_terms.length > 0) {
        setContractTerms(res.data.contract_terms);
      }
    } catch (error) {
      console.error('Failed to fetch contract terms');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContractTermChange = (term) => {
    if (!formData.start_date) {
      toast.error('Please select a start date first');
      return;
    }

    const startDate = new Date(formData.start_date);
    const years = parseInt(term.replace('yr', ''));
    const endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + years);
    
    const formattedEndDate = endDate.toISOString().split('T')[0];
    
    setFormData(prev => ({
      ...prev,
      contract_term: term,
      end_date: formattedEndDate
    }));
    
    toast.success(`End date set to ${years} year${years > 1 ? 's' : ''} from start date`);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a contract title');
      return;
    }

    const cleanContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!cleanContent) {
      toast.error('Please enter contract content');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        document_type: formData.document_type,
        content: formData.content,
        client_id: formData.client_id || null,
        requires_signature: formData.requires_signature,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        value: formData.value ? parseFloat(formData.value) : null,
        currency: formData.currency,
        status: formData.status
      };

      await api.put(`/contracts/${id}`, payload);
      toast.success('Contract updated successfully');
      navigate(`/contracts/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contract');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/contracts/${id}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contract
        </Button>
        <h1 className="text-2xl font-bold text-slate-800">Edit Contract</h1>
        <p className="text-sm text-slate-500 mt-1">
          Update contract details and content
        </p>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Contract Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Annual IT Support Agreement"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="document_type">Document Type</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(value) => handleChange('document_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="work_order">Work Order</SelectItem>
                    <SelectItem value="project_document">Project Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="client_id">Client</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => handleChange('client_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="end_date">End Date</Label>
                <div className="flex gap-2">
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={formData.contract_term || ''}
                    onValueChange={(value) => handleContractTermChange(value)}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Quick set" />
                    </SelectTrigger>
                    <SelectContent>
                      {contractTerms.map(term => {
                        const years = parseInt(term.replace('yr', ''));
                        return (
                          <SelectItem key={term} value={term}>
                            {years} Year{years > 1 ? 's' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="value">Contract Value</Label>
                <Input
                  id="value"
                  type="number"
                  placeholder="0.00"
                  value={formData.value}
                  onChange={(e) => handleChange('value', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleChange('currency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_signature"
                checked={formData.requires_signature}
                onChange={(e) => handleChange('requires_signature', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="requires_signature" className="font-normal cursor-pointer">
                This contract requires signature
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Content *</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => handleChange('content', value)}
                placeholder="Enter contract content here..."
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'indent': '-1'}, { 'indent': '+1' }],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
                style={{ minHeight: '300px' }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate(`/contracts/${id}`)}
            variant="outline"
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditContract;
